import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function SolutionAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const bids = [
    { amount: '£100', color: '#10b981', delay: 0.5 },
    { amount: '£105', color: '#f59e0b', delay: 0.8 },
    { amount: '£110', color: '#8b5cf6', delay: 1.1 },
  ];

  return (
    <div ref={ref} className="relative w-full h-72 md:h-80">
      <svg viewBox="0 0 400 220" className="w-full h-full">
        {/* Queue container - isometric style */}
        <motion.g
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.5 }}
        >
          {/* Queue base */}
          <polygon
            points="50,100 200,100 200,160 50,160"
            fill="#f3f4f6"
            stroke="#d1d5db"
            strokeWidth="2"
          />
          {/* Queue label */}
          <text x="125" y="85" textAnchor="middle" fontSize="11" fill="#6b7280" fontWeight="500">
            Auction Mailbox
          </text>
        </motion.g>

        {/* Bids entering queue */}
        {bids.map((bid, i) => (
          <motion.g
            key={i}
            initial={{ x: -80, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : { x: -80, opacity: 0 }}
            transition={{ duration: 0.5, delay: bid.delay }}
          >
            <rect
              x={60 + i * 48}
              y={115}
              width="42"
              height="30"
              rx="4"
              fill={bid.color}
              stroke={bid.color}
              strokeWidth="1"
            />
            <text
              x={81 + i * 48}
              y={135}
              textAnchor="middle"
              fill="white"
              fontSize="11"
              fontWeight="bold"
            >
              {bid.amount}
            </text>
          </motion.g>
        ))}

        {/* Arrow from queue to processor */}
        <motion.path
          d="M 205 130 L 240 130"
          stroke="#9ca3af"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="6 4"
          initial={{ pathLength: 0 }}
          animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
          transition={{ duration: 0.4, delay: 1.5 }}
        />
        <motion.polygon
          points="245,130 235,125 235,135"
          fill="#9ca3af"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 1.8 }}
        />

        {/* Actor processor - isometric cube */}
        <motion.g
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.5, delay: 0.3, type: 'spring' }}
        >
          {/* Top face */}
          <polygon
            points="300,70 350,95 300,120 250,95"
            fill="#3b82f6"
            stroke="#1d4ed8"
            strokeWidth="2"
          />
          {/* Right face */}
          <polygon
            points="300,120 350,95 350,145 300,170"
            fill="#2563eb"
            stroke="#1d4ed8"
            strokeWidth="2"
          />
          {/* Left face */}
          <polygon
            points="300,120 250,95 250,145 300,170"
            fill="#1d4ed8"
            stroke="#1e40af"
            strokeWidth="2"
          />
          {/* Actor label */}
          <text x="300" y="100" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">
            Auction
          </text>
          <text x="300" y="113" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">
            Actor
          </text>
        </motion.g>

        {/* Processing indicator */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: [0, 1, 1, 0] } : { opacity: 0 }}
          transition={{ delay: 2, duration: 2, repeat: Infinity, repeatDelay: 1 }}
        >
          <circle cx="300" cy="155" r="8" fill="#22c55e" />
          <text x="300" y="159" textAnchor="middle" fontSize="8" fill="white" fontWeight="bold">OK</text>
        </motion.g>

        {/* "One at a time" label */}
        <motion.g
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ delay: 2.2 }}
        >
          <text x="300" y="195" textAnchor="middle" fontSize="12" fill="#374151" fontWeight="500">
            One at a time, in order
          </text>
        </motion.g>

        {/* Numbers showing order */}
        {bids.map((_, i) => (
          <motion.g
            key={`num-${i}`}
            initial={{ opacity: 0, scale: 0 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
            transition={{ delay: 1.4 + i * 0.2, type: 'spring' }}
          >
            <circle cx={81 + i * 48} cy={108} r="10" fill="#1f2937" />
            <text x={81 + i * 48} y={112} textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">
              {i + 1}
            </text>
          </motion.g>
        ))}
      </svg>
    </div>
  );
}
